import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import VinculoModal from './components/modals/VinculoModal';
import usePagination10 from './components/hooks/usePagination10';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = { cnpj: '', cpf: '', tipo: '' };

function Vinculos() {
  const token = localStorage.getItem('token');
  const location = useLocation();
  const [usuario, setUsuario] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [escolas, setEscolas] = useState([]);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const displayed = items.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  // Busca todas as escolas para o select do modal
  useEffect(() => {
    axios.get(`${API_BASE_URL.ESCOLA}/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setEscolas(r.data))
      .catch(() => {});
  }, [token]);

  // Recebe o usuário pelo state da navegação
  useEffect(() => {
    if (location.state?.usuario) {
      setUsuario(location.state.usuario);
    }
  }, [location.state]);

  // Carrega os vínculos do usuário
  useEffect(() => {
    if (!usuario?.cpf) return;
    setIsLoading(true);
    axios.get(`${API_BASE_URL.VINCULO}/cpf`, {
      params: { cpf: usuario.cpf }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        setItems(r.data);
      })
      .catch(err => {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar vínculos.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [usuario, token, atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  function handleNew() {
    setForm({ ...emptyForm, cpf: usuario?.cpf || '' });
    setEditMode(false);
    setShowModal(true);
  }

  function handleEdit() {
    if (selected.length !== 1) return;
    const s = selected[0];
    setForm({ cnpj: s.cnpj, cpf: s.cpf, tipo: s.tipo });
    setEditMode(true);
    setShowModal(true);
  }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.VINCULO}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { setShowModal(false); setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao salvar vínculo.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(
      selected.map(s => axios.delete(
        `${API_BASE_URL.VINCULO}/delete/${s.cpf}/${s.cnpj}`,
        { headers: { Authorization: `Bearer ${token}` } }
      ))
    ).then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir vínculo.'))
    );
  }

  function keyOf(item) { return `${item.cpf}_${item.cnpj}`; }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  const tipoLabel = tipo => ({ ADMIN: 'ADMINISTRADOR', GEST: 'GESTOR', USER: 'USUÁRIO' }[tipo] || tipo);

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="VÍNCULOS" icon="bi-link-45deg" />
        <PageContent>
          {usuario && (
            <div className="alert alert-light border mb-3 py-2 small">
              <i className="bi bi-person me-1"></i>
              <strong>{usuario.nome}</strong> &nbsp;|&nbsp; CPF: {usuario.cpf} &nbsp;|&nbsp; Login: {usuario.login}
            </div>
          )}
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
          </Toolbar>
          <TableContainer isLoading={false}>
              <DataTable head={
                  <tr>
                    <th>#</th>
                    <th>Entidade</th>
                    <th>Município</th>
                    <th>UF</th>
                    <th>Tipo de Vínculo</th>
                  </tr>
                }>
                <tbody>
                  {displayed.map((item, idx) => (
                    <tr
                      key={keyOf(item)}
                      className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''}
                      onClick={() => toggleSelect(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{setPagination.startIndex + idx + 1}</td>
                      <td>{item.escola?.nome}</td>
                      <td>{item.escola?.municipio}</td>
                      <td>{item.escola?.uf}</td>
                      <td>{tipoLabel(item.tipo)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <VinculoModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSave}
        form={form}
        handleChange={handleChange}
        escolas={escolas}
        title={editMode ? 'EDITAR VÍNCULO' : 'NOVO VÍNCULO'}
      />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Vinculos;
